import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Add } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import AIModelClassLabel from 'src/features/ai_model/components/AIModelClassLabel';
import {
	IAIModelClassLabelsProps,
	IClassLabelRow,
} from 'src/features/ai_model/domain/types';
import {
	updateAIModel,
	AI_MODEL_LOADING_KEY,
} from 'src/features/ai_model/store';
import { AppDispatch, RootState } from 'src/store';

const TEMP_ROW_ID = 'new';

const convertClassLabelsToRows = (
	classLabels: Record<string, string> | null | undefined,
): IClassLabelRow[] => {
	if (!classLabels) return [];

	return Object.entries(classLabels).map(([classId, label]) => ({
		id: classId,
		classId,
		label,
		initialClassId: classId,
		initialLabel: label,
	}));
};

const AIModelClassLabels: React.FC<IAIModelClassLabelsProps> = ({
	aiModel,
	siteSlug,
	onAiModelUpdate,
	hasAIModelFiles,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[AI_MODEL_LOADING_KEY],
	);

	const [classLabelRows, setClassLabelRows] = useState<IClassLabelRow[]>([]);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedRowToDelete, setSelectedRowToDelete] =
		useState<IClassLabelRow | null>(null);

	const disabled = !aiModel;

	const tempRow = classLabelRows.find((row) => row.id === TEMP_ROW_ID);

	const handleAddClassLabel = () => {
		if (tempRow) {
			snackbar(
				'Save or remove the unsaved class label before adding a new one',
				'warning',
			);
			return;
		}
		setClassLabelRows((prev) => [
			...prev,
			{
				id: TEMP_ROW_ID,
				classId: '',
				label: '',
				initialClassId: '',
				initialLabel: '',
			},
		]);
	};

	const handleClassLabelInputChange = (
		rowId: string,
		field: 'classId' | 'label',
		value: string,
	) => {
		setClassLabelRows((prev) =>
			prev.map((row) =>
				row.id === rowId ? { ...row, [field]: value } : row,
			),
		);
	};

	const isClassLabelRowChanged = (row: IClassLabelRow): boolean => {
		const currentClassId = row.classId.trim();
		const currentLabel = row.label.trim();
		const initialClassId = row.initialClassId?.trim() ?? '';
		const initialLabel = row.initialLabel?.trim() ?? '';

		return (
			currentClassId !== initialClassId || currentLabel !== initialLabel
		);
	};

	const persistClassLabels = (
		classLabels: Record<string, string> | null,
		action: 'delete' | 'update',
		affectedRowId: string,
	) => {
		if (!aiModel || !siteSlug) return;

		const rowsSnapshot = [...classLabelRows];

		dispatch(
			updateAIModel({
				id: aiModel.id,
				siteSlug,
				request: { class_labels: classLabels ?? {} },
				onSuccess: (updatedModel) => {
					onAiModelUpdate(updatedModel);

					if (action === 'delete') {
						setClassLabelRows(
							rowsSnapshot.filter((r) => r.id !== affectedRowId),
						);
						setIsDeleteModalOpen(false);
						setSelectedRowToDelete(null);
					} else {
						const affectedRow = rowsSnapshot.find(
							(r) => r.id === affectedRowId,
						);
						if (affectedRow) {
							const savedClassId = affectedRow.classId.trim();
							const savedLabel = affectedRow.label.trim();

							setClassLabelRows(
								rowsSnapshot.map((r) => {
									if (r.id !== affectedRowId) return r;
									return {
										...r,
										id: savedClassId,
										classId: savedClassId,
										label: savedLabel,
										initialClassId: savedClassId,
										initialLabel: savedLabel,
									};
								}),
							);
						}
					}

					snackbar(`Class labels ${action}d successfully`, 'success');
				},
			}),
		);
	};

	const handleUpdateClassLabelRow = (rowId: string) => {
		if (!aiModel || !siteSlug) return;

		const row = classLabelRows.find((item) => item.id === rowId);
		if (!row || !isClassLabelRowChanged(row)) return;

		const trimmedClassId = row.classId.trim();
		const trimmedLabel = row.label.trim();

		if (!trimmedClassId || !trimmedLabel) {
			snackbar('Class ID and Label are required', 'error');
			return;
		}

		const isDuplicate = classLabelRows.some(
			(r) => r.id !== rowId && r.classId.trim() === trimmedClassId,
		);
		if (isDuplicate) {
			snackbar('Class ID must be unique', 'error');
			return;
		}

		const updated = { ...(aiModel.class_labels ?? {}) };
		const oldClassId = row.initialClassId?.trim();

		if (oldClassId && oldClassId !== trimmedClassId) {
			delete updated[oldClassId];
		}

		updated[trimmedClassId] = trimmedLabel;

		persistClassLabels(updated, 'update', rowId);
	};

	const handleRemoveClassLabelRow = (rowId: string) => {
		const rowToRemove = classLabelRows.find((row) => row.id === rowId);
		if (!rowToRemove) return;

		if (hasAIModelFiles && rowToRemove.initialClassId?.trim()) {
			const persistedCount = classLabelRows.filter((r) =>
				Boolean(r.initialClassId?.trim()),
			).length;

			if (persistedCount === 1) {
				snackbar(
					'There has to be at least one class label map as there are AI model files used for inference on mobile app',
					'warning',
				);
				return;
			}
		}

		setSelectedRowToDelete(rowToRemove);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDeleteClassLabel = () => {
		if (!selectedRowToDelete || !aiModel || !siteSlug) {
			setIsDeleteModalOpen(false);
			setSelectedRowToDelete(null);
			return;
		}

		const rowId = selectedRowToDelete.id;
		const persistedKey = selectedRowToDelete.initialClassId?.trim();

		if (!persistedKey) {
			setClassLabelRows((prev) => prev.filter((row) => row.id !== rowId));
			setIsDeleteModalOpen(false);
			setSelectedRowToDelete(null);
			return;
		}

		const updated = { ...(aiModel.class_labels ?? {}) };
		delete updated[persistedKey];

		persistClassLabels(
			Object.keys(updated).length > 0 ? updated : null,
			'delete',
			rowId,
		);
	};

	useEffect(() => {
		if (!aiModel) {
			setClassLabelRows([]);
			return;
		}
		setClassLabelRows(convertClassLabelsToRows(aiModel.class_labels));
	}, [aiModel?.id]);

	return (
		<Box
			sx={{
				backgroundColor: 'white',
				padding: 2,
				borderRadius: '4px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2,
				}}
			>
				<Typography variant='h6'>Class labels</Typography>
				<Button
					variant='outlined'
					startIcon={<Add />}
					onClick={handleAddClassLabel}
					disabled={isLoading || disabled}
				>
					Add Class Label
				</Button>
			</Box>
			{classLabelRows.length === 0 ? (
				<Typography variant='subtitle1' color={'rgb(0,0,0,0.6)'} p={3}>
					No class labels yet.
				</Typography>
			) : (
				<>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr 2fr 0.5fr',
							gap: 2,
							px: 1,
							mb: 1.5,
						}}
					>
						<Typography variant='subtitle2'>Class ID</Typography>
						<Typography variant='subtitle2'>Label</Typography>
						<Typography alignSelf={'center'} variant='subtitle2'>
							Actions
						</Typography>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1.5,
						}}
					>
						{classLabelRows.map((row) => (
							<AIModelClassLabel
								key={row.id}
								row={row}
								isLoading={isLoading}
								disabled={disabled}
								isTemp={row.id === TEMP_ROW_ID}
								canUpdate={isClassLabelRowChanged(row)}
								onClassLabelInputChange={
									handleClassLabelInputChange
								}
								onUpdateClassLabelRow={
									handleUpdateClassLabelRow
								}
								onRemoveClassLabelRow={
									handleRemoveClassLabelRow
								}
							/>
						))}
					</Box>
				</>
			)}

			<ConfirmationModal
				modalOpen={isDeleteModalOpen}
				handleModalCLose={() => {
					setIsDeleteModalOpen(false);
					setSelectedRowToDelete(null);
				}}
				title='Delete Class Label'
				modalContent={`Are you sure you want to delete the class label "${selectedRowToDelete?.label}"?`}
				handleYes={handleConfirmDeleteClassLabel}
				handleNo={() => {
					setIsDeleteModalOpen(false);
					setSelectedRowToDelete(null);
				}}
				modalYesBtnTitle='DELETE'
				loadingKey={AI_MODEL_LOADING_KEY}
			/>
		</Box>
	);
};

export default AIModelClassLabels;
